/**
 * ตัวช่วยแสดงผลของป้าย Kanban การผลิต — ป้ายสถานะ, สีของแถบ Tag Color, ข้อความ
 * ข้อผิดพลาดใต้แถว (ภาษาไทย) และตัวกรองใบสั่งผลิตที่เลือกพิมพ์ป้ายได้ (D-169, D-170)
 *
 * แยกจาก `OutputTagScreen.tsx`/`KanbanTagSheet.tsx` ด้วยเหตุผลเดียวกับ
 * `desktop/productionOrders/productionOrderView.ts`: ฟังก์ชันล้วน ทดสอบได้โดยไม่ต้อง
 * เรนเดอร์ React
 */

import type { StatusTone } from '../../desktop/StatusBadge';
import type { OutputTagFailure } from './client';
import {
  NO_ACTIVE_PACKING_STANDARD,
  OUTPUT_TAG_ALREADY_ACTIVE,
  OUTPUT_TAG_NOT_ACTIVE,
  PRODUCTION_ORDER_NOT_RELEASED,
  TOO_MANY_PACKAGES,
  type OutputTag,
} from './types';
import type { ProductionOrder } from '../productionOrders/types';

/* ------------------------------------------------------------- ป้ายกำกับ -- */

/**
 * ป้ายสถานะภาษาไทยของป้าย — สถานะที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ" ด้วยเหตุผล
 * เดียวกับ `orderStatusLabel` ของใบสั่งผลิต
 */
export function outputTagStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'ใช้งานอยู่';
    case 'REVOKED':
      return 'ถูกยกเลิก';
    case 'REPRINTED':
      return 'ถูกพิมพ์ซ้ำแล้ว';
    case 'CONSUMED':
      return 'ใช้ไปแล้ว';
    default:
      return status;
  }
}

export function outputTagStatusTone(status: string): StatusTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'REVOKED':
      return 'danger';
    case 'REPRINTED':
      return 'neutral';
    case 'CONSUMED':
      return 'info';
    default:
      return 'neutral';
  }
}

export function outputTagStageLabel(stage: string): string {
  switch (stage) {
    case 'WIP':
      return 'งานระหว่างผลิต (WIP)';
    case 'FG':
      return 'สินค้าสำเร็จ (FG)';
    default:
      return stage;
  }
}

/* --------------------------------------------------------------- สี Tag -- */

/**
 * ชุดสีที่ตกลงกับ Store ไว้แล้ว (D-170 ข้อ 2) — ค่าอื่นหรือค่าว่างแสดงเป็นเทาเสมอ
 * ไม่เดาสีเองเมื่อเจอค่าที่ไม่รู้จัก (เทาคือ "ไม่ทราบสี" ไม่ใช่สีที่เจ็ดของจริง)
 */
const TAG_COLOR_CSS: Readonly<Record<string, string>> = {
  GREEN: '#2e7d32',
  BLUE: '#1565c0',
  RED: '#c62828',
  YELLOW: '#f9a825',
  ORANGE: '#ef6c00',
  WHITE: '#ffffff',
  PURPLE: '#6a1b9a',
};
const TAG_COLOR_FALLBACK = '#9e9e9e';

export function tagColorCss(tagColor: string | null): string {
  if (tagColor === null) {
    return TAG_COLOR_FALLBACK;
  }
  return TAG_COLOR_CSS[tagColor] ?? TAG_COLOR_FALLBACK;
}

/** ตัวหนังสือบนแถบสีต้องอ่านออกได้เสมอ — พื้นขาวใช้ตัวหนังสือดำ ที่เหลือใช้ขาว */
export function tagColorTextCss(tagColor: string | null): string {
  return tagColor === 'WHITE' ? '#1a1a1a' : '#ffffff';
}

/* ------------------------------------------------------- เลือกใบสั่งผลิต -- */

/** เลือกพิมพ์ป้ายได้เฉพาะใบที่ปล่อยแล้ว (D-170 invariant) */
export function isOrderSelectableForTag(status: string): boolean {
  return status === 'RELEASED' || status === 'IN_PROCESS';
}

export function selectableOrdersForTag(
  orders: readonly ProductionOrder[],
): readonly ProductionOrder[] {
  return orders.filter((order) => isOrderSelectableForTag(order.status));
}

/** กรองด้วยเลขใบสั่งที่พิมพ์ค้างไว้ — ว่าง = ไม่กรอง ("กรอกเลขใบสั่งแล้วค้น") */
export function filterOrdersByOrderNo(
  orders: readonly ProductionOrder[],
  query: string,
): readonly ProductionOrder[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return orders;
  }
  return orders.filter((order) => (order.order_no ?? '').toLowerCase().includes(needle));
}

/* --------------------------------------------------------- แผนที่ป้าย -- */

/** ทุกป้ายของแต่ละ operation ไม่ว่าสถานะใด — หนึ่ง operation มีได้หลายป้าย (หลายแพ็คเกจ) */
export function outputTagsByOperationSeq(
  tags: readonly OutputTag[],
): ReadonlyMap<number, readonly OutputTag[]> {
  const map = new Map<number, OutputTag[]>();
  for (const tag of tags) {
    const list = map.get(tag.operation_seq);
    if (list) {
      list.push(tag);
    } else {
      map.set(tag.operation_seq, [tag]);
    }
  }
  return map;
}

/** ป้ายที่ถูกแทนที่ไปแล้ว — REPRINTED ชี้ไปป้ายใหม่, REVOKED ถูกยกเลิก (D-170 ข้อ 4) */
export function isOldOutputTag(tag: OutputTag): boolean {
  return tag.status === 'REPRINTED' || tag.status === 'REVOKED';
}

/** แพ็คเกจที่ยังใช้แทนอยู่ (ไม่ใช่ป้ายเก่า) เรียงตาม package_seq */
export function currentOutputTags(tags: readonly OutputTag[]): readonly OutputTag[] {
  return tags
    .filter((tag) => !isOldOutputTag(tag))
    .slice()
    .sort((a, b) => a.package_seq - b.package_seq);
}

/** ป้ายเก่า (REPRINTED/REVOKED) ของ operation — แสดงแยกกลุ่ม "ป้ายเก่า" */
export function oldOutputTags(tags: readonly OutputTag[]): readonly OutputTag[] {
  return tags.filter(isOldOutputTag);
}

/**
 * ยังมีแพ็คเกจที่ไม่มีป้ายหรือไม่ — ยังไม่มีป้ายเลยถือว่า "ไม่ทราบ" จึงแสดงปุ่มพิมพ์
 * ป้ายเสมอ (D-170 invariant: ถ้าทุกแพ็คเกจมีป้ายแล้ว เซิร์ฟเวอร์อธิบายเองด้วย
 * `409 OUTPUT_TAG_ALREADY_ACTIVE`)
 */
export function hasUntaggedPackages(currentTags: readonly OutputTag[]): boolean {
  if (currentTags.length === 0) {
    return true;
  }
  return currentTags.length < currentTags[0].package_count;
}

/* --------------------------------------------------------- ข้อผิดพลาด -- */

/**
 * ข้อความภาษาไทยหนึ่งแถว หนึ่งสถานะ — ห้ามยุบเป็น "ทำรายการไม่สำเร็จ" ก้อนเดียว
 * ด้วยเหตุผลเดียวกับ `ProductionOrderFailureNotice`: ผู้ใช้ที่ไม่รู้ว่าชนกฎข้อไหน
 * จะแก้ไม่ถูก
 */
export function outputTagFailureMessage(failure: OutputTagFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่';
    case 'forbidden':
      return 'คุณไม่มีสิทธิ์ทำรายการนี้ในโรงงานนี้';
    case 'not-found':
      return 'ไม่พบใบสั่งผลิต ขั้นตอนการผลิต หรือป้ายนี้ — อาจถูกลบหรือย้ายโรงงานไปแล้ว';
    case 'conflict': {
      const code = failure.error.error_code;
      if (code === PRODUCTION_ORDER_NOT_RELEASED) {
        return 'ใบสั่งผลิตนี้ยังไม่ถูกสั่งปล่อย พิมพ์ป้ายได้เฉพาะใบที่ปล่อยแล้ว';
      }
      if (code === OUTPUT_TAG_ALREADY_ACTIVE) {
        return 'ทุกแพ็คเกจของขั้นนี้มีป้ายแล้ว ใช้ปุ่ม "พิมพ์ซ้ำ" ถ้าป้ายใบใดสูญหายหรือเสียหาย';
      }
      if (code === OUTPUT_TAG_NOT_ACTIVE) {
        return 'ป้ายนี้ไม่ได้อยู่ในสถานะใช้งานแล้ว พิมพ์ซ้ำไม่ได้';
      }
      return failure.error.message ?? 'ทำรายการไม่สำเร็จเพราะข้อมูลเปลี่ยนไปแล้ว';
    }
    case 'rule-violation': {
      const code = failure.error.error_code;
      if (code === NO_ACTIVE_PACKING_STANDARD) {
        return 'สินค้านี้ยังไม่มีมาตรฐานการบรรจุ (packing standard) ที่ใช้งานอยู่ — ตั้งค่าก่อนพิมพ์ป้าย';
      }
      if (code === TOO_MANY_PACKAGES) {
        return 'จำนวนแพ็คเกจของ operation นี้เกิน 500 แพ็คเกจ — ตรวจสอบจำนวนที่สั่งผลิตหรือ Qty/Pkg';
      }
      return failure.error.message ?? 'ทำรายการไม่สำเร็จเพราะข้อมูลไม่ผ่านกฎทางธุรกิจ';
    }
    case 'bad-request':
      return failure.error.message ?? 'คำขอไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ผิดพลาด (${failure.httpStatus})`;
    case 'network-error':
      return `เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ (${failure.detail})`;
    case 'invalid-response':
      return 'เซิร์ฟเวอร์ตอบข้อมูลที่อ่านไม่ได้';
    case 'cancelled':
      return '';
  }
}
